import type {
  Experiment,
  ExperimentTreatmentType,
  ExperimentTreatmentValue,
  FeatureRollout,
  YammerState,
} from '@ms/yammer-data/dist/state/types';
import { noop } from 'lodash';
import type { FC, PropsWithChildren } from 'react';
import React, { createContext, memo, useContext, useEffect, useMemo } from 'react';

export interface ExperimentContextType {
  /**
   * Reads the treatment without reporting exposure. Pair with useReportExperimentTreatmentCallback
   * when exposure occurs at an interaction or in an eligible context rather than on mount.
   */
  readonly useExperimentTreatment: <E extends Experiment>(experiment: E) => ExperimentTreatmentType<E>;
  /**
   * Returns a callback for reporting exposure to the supplied treatment; calling the hook does not report.
   * Invoke the callback when the user reaches the measured behavior, subject to reporting eligibility.
   */
  readonly useReportExperimentTreatmentCallback: (
    experiment: Experiment,
    treatment: ExperimentTreatmentValue
  ) => () => void;
  readonly useIsFeatureRolloutEnabled: (featureRollout: FeatureRollout) => boolean;
  /**
   * Reads the treatment and requests exposure reporting after mount and when the treatment changes, not during render.
   * Use when mounting the consumer represents exposure; otherwise use useExperimentTreatment with
   * useReportExperimentTreatmentCallback to report at the relevant interaction or context.
   */
  readonly useAndReportExperimentTreatment: <E extends Experiment>(experiment: E) => ExperimentTreatmentType<E>;
  /** Reads the treatment from the supplied state without subscribing to updates or reporting exposure. */
  readonly getExperimentTreatment: <E extends Experiment>(
    state: YammerState,
    experiment: E
  ) => ExperimentTreatmentType<E>;
}

const ExperimentContext = createContext<ExperimentContextType | undefined>(undefined);

const useExperimentContext = () => {
  const experimentContext = useContext(ExperimentContext);

  if (experimentContext == null) {
    throw new Error('ExperimentContext has not been initialized');
  }

  return experimentContext;
};

type UseExperimentTreatment = ExperimentContextType['useExperimentTreatment'];
export const useExperimentTreatment: UseExperimentTreatment = (experiment) =>
  useExperimentContext().useExperimentTreatment(experiment);

type UseReportExperimentTreatmentCallback = ExperimentContextType['useReportExperimentTreatmentCallback'];
export const useReportExperimentTreatmentCallback: UseReportExperimentTreatmentCallback = (experiment, treatment) =>
  useExperimentContext().useReportExperimentTreatmentCallback(experiment, treatment);

type UseIsFeatureRolloutEnabled = ExperimentContextType['useIsFeatureRolloutEnabled'];
export const useIsFeatureRolloutEnabled: UseIsFeatureRolloutEnabled = (featureRollout) =>
  useExperimentContext().useIsFeatureRolloutEnabled(featureRollout);

type UseAndReportExperimentTreatment = <E extends Experiment>(experiment: E) => ExperimentTreatmentType<E>;
export const useAndReportExperimentTreatment: UseAndReportExperimentTreatment = (experiment) => {
  const experimentTreatment = useExperimentTreatment(experiment);
  const reportExperimentTreatment = useReportExperimentTreatmentCallback(experiment, experimentTreatment);

  useEffect(reportExperimentTreatment, [experimentTreatment, reportExperimentTreatment]);

  return experimentTreatment;
};

type ExperimentContextProviderProps = Omit<ExperimentContextType, 'getExperimentTreatment'> &
  Partial<Pick<ExperimentContextType, 'getExperimentTreatment'>>;

export const ExperimentContextProvider: FC<PropsWithChildren<ExperimentContextProviderProps>> = memo(
  ({
    children,
    useExperimentTreatment,
    useReportExperimentTreatmentCallback,
    useIsFeatureRolloutEnabled,
    useAndReportExperimentTreatment,
    getExperimentTreatment = noop as ExperimentContextType['getExperimentTreatment'],
  }) => {
    const experimentContext = useMemo(
      () => ({
        useExperimentTreatment,
        getExperimentTreatment,
        useReportExperimentTreatmentCallback,
        useIsFeatureRolloutEnabled,
        useAndReportExperimentTreatment,
      }),
      [
        useExperimentTreatment,
        getExperimentTreatment,
        useReportExperimentTreatmentCallback,
        useIsFeatureRolloutEnabled,
        useAndReportExperimentTreatment,
      ]
    );

    return <ExperimentContext.Provider value={experimentContext}>{children}</ExperimentContext.Provider>;
  }
);
